import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import { buildDefaultSprintPayload } from '../../utils/sprintDefaults';
import { useNexusSync } from '../../context/NexusSyncContext';
import { focusParent, withMention, withoutMention } from './team';
import { describeIterationChange, shortDay } from './roadmap/changes';

// Données de la nouvelle fiche projet et ce qu'on peut y modifier (mêmes routes que l'ancienne fiche). Chaque onglet
// secondaire (journal, finances…) charge lui-même ses données ; ici, ce que l'en-tête, l'aperçu et la roadmap partagent.

const DRIVE_SYNC_TTL_MS = 15 * 60 * 1000;
const list = (data) => (Array.isArray(data) ? data : data?.items || []);
const byStart = (a, b) => String(a.start_date || '').localeCompare(String(b.start_date || ''));

const normalizeIterations = (data) => list(data).slice().sort(byStart).map((iteration, index) => ({
  ...iteration,
  name: iteration.name || `Itération ${index + 1}`,
  sprints: (iteration.sprints || []).slice().sort(byStart)
    .map((sprint, sprintIndex) => ({ ...sprint, name: sprint.description || `Sprint ${sprintIndex + 1}` })),
}));

export default function useProjectData(projectId, user) {
  const navigate = useNavigate();
  const { lastMutation } = useNexusSync();
  const [project, setProject] = useState(null);
  const [iterations, setIterations] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [slots, setSlots] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [circles, setCircles] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [targets, setTargets] = useState([]);
  const [focus, setFocus] = useState([]);
  const [overview, setOverview] = useState(null);
  const [domains, setDomains] = useState({}); // domaines à effet du projet : { roadmap: { can, holders }, … }
  const [billability, setBillability] = useState({}); // { iterationId: { billable, decided_by_name, decided_at } }
  const [steering, setSteering] = useState(null); // budget consommé / budgété, % de réalisation (GET /steering)
  const [changes, setChanges] = useState(null); // historique tracé de la roadmap et du budget, chargé à la demande
  const [frame, setFrame] = useState(null); // cadre du projet : { items: [puces], updated_by_name, updated_at }
  const [reasonRequest, setReasonRequest] = useState(null); // motif demandé (ReasonDialog)
  const reasonAnswer = useRef(null);
  const [error, setError] = useState(null);
  const errorTimer = useRef(null);

  const fail = useCallback((message) => {
    setError(message);
    clearTimeout(errorTimer.current);
    errorTimer.current = setTimeout(() => setError(null), 6000);
  }, []);
  useEffect(() => () => clearTimeout(errorTimer.current), []);

  // Une action de la fiche : l'erreur s'affiche dans le bandeau, l'appelant sait si c'est passé.
  const attempt = useCallback(async (label, action) => {
    try {
      return await action();
    } catch (err) {
      if (!err?.cancelled) fail(`${label} : ${err?.message || 'erreur inconnue'}`); // motif refusé : rien à signaler
      throw err;
    }
  }, [fail]);

  const loadProject = useCallback(async () => {
    try {
      setProject(await apiService.get(`/projects/${projectId}`));
    } catch (err) {
      if (/acc[eè]s interdit/i.test(err?.message || '')) {
        navigate('/projects', { replace: true, state: { accessDeniedMessage: "Vous n'avez pas accès à ce projet." } });
        return;
      }
      fail(err?.message || 'Projet introuvable');
    }
  }, [projectId, navigate, fail]);

  const loadIterations = useCallback(async () => {
    setIterations(normalizeIterations(await apiService.get(`projects/${projectId}/iterations`).catch(() => [])));
  }, [projectId]);
  const loadObjectives = useCallback(async () => {
    setObjectives(list(await apiService.get(`/projects/${projectId}/objectives`).catch(() => [])));
  }, [projectId]);
  const loadTargets = useCallback(async () => {
    setTargets(list(await apiService.getProjectAttributeTargets(projectId).catch(() => [])));
  }, [projectId]);
  const loadAttributes = useCallback(async () => {
    setAttributes(list(await apiService.get(`/projects/${projectId}/attributes`).catch(() => [])));
  }, [projectId]);
  const loadDomains = useCallback(async () => {
    setDomains(await apiService.get(`/projects/${projectId}/domains`).catch(() => ({})) || {});
  }, [projectId]);
  const loadBillability = useCallback(async () => {
    setBillability(await apiService.get(`/projects/${projectId}/billability`).catch(() => ({})) || {});
  }, [projectId]);
  const loadSteering = useCallback(async () => {
    setSteering(await apiService.get(`/projects/${projectId}/steering`).catch(() => null));
  }, [projectId]);
  const loadFrame = useCallback(async () => {
    setFrame(await apiService.get(`/projects/${projectId}/frame`).catch(() => ({ items: [] })));
  }, [projectId]);
  const loadChanges = useCallback(async () => {
    setChanges(await apiService.get(`/projects/${projectId}/domain-changes?limit=200`).catch(() => []) || []);
  }, [projectId]);
  const loadOverview = useCallback(async () => {
    try {
      setOverview(await apiService.get(`/projects/${projectId}/overview`));
    } catch (err) {
      setOverview({ error: err?.message || 'Aperçu indisponible' });
    }
  }, [projectId]);

  useEffect(() => {
    setProject(null);
    setOverview(null);
    apiService.post(`/home/visits/${projectId}`, {}).catch(() => {});
    loadProject();
    loadIterations();
    loadObjectives();
    loadTargets();
    loadAttributes();
    loadOverview();
    loadDomains();
    loadBillability();
    loadSteering();
    loadFrame();
    setChanges(null);
    apiService.get('/slots').then((d) => setSlots(list(d).slice().sort(byStart))).catch(() => setSlots([]));
    apiService.get('/employees').then((d) => setEmployees(list(d))).catch(() => setEmployees([]));
    apiService.get('/clients').then((d) => setClients(list(d))).catch(() => setClients([]));
    apiService.get('/circles').then((d) => setCircles(list(d))).catch(() => setCircles([]));
  }, [projectId, loadProject, loadIterations, loadObjectives, loadTargets, loadAttributes, loadOverview, loadDomains,
    loadBillability, loadSteering, loadFrame]);

  // Les documents du Drive du projet entrent dans la base de connaissances (au plus tous les quarts d'heure).
  useEffect(() => {
    const key = `knowledge_drive_sync:${user?.id || user?.email || 'current'}:${projectId}`;
    try {
      if (Date.now() - Number(localStorage.getItem(key) || 0) < DRIVE_SYNC_TTL_MS) return;
      localStorage.setItem(key, String(Date.now()));
    } catch {
      // stockage indisponible : la synchronisation reste au mieux
    }
    apiService.syncKnowledgeProjectDrive(projectId).catch(() => {});
  }, [projectId, user?.id, user?.email]);

  const parentCircle = useMemo(() => focusParent(project, circles), [project, circles]);
  const loadFocus = useCallback(async () => {
    if (parentCircle == null) { setFocus([]); return; }
    setFocus(list(await apiService.get(`circle-focus?parent_id=${parentCircle}`).catch(() => [])));
  }, [parentCircle]);
  useEffect(() => { loadFocus(); }, [loadFocus]);

  // Nexus a modifié le projet (outil MCP) : on recharge ce qui a bougé.
  useEffect(() => {
    const meta = lastMutation?.metadata;
    if (!meta) return;
    const raw = meta.project_id ?? meta.projectId;
    const target = raw == null ? null : Number(raw);
    if (target == null ? lastMutation.source !== 'mcp' : target !== Number(projectId)) return;
    const entity = String(meta.entity || '').toLowerCase();
    if (entity === 'projects') loadProject();
    if (['iterations', 'sprints', 'timeline'].includes(entity)) loadIterations();
    if (entity === 'objectives' || entity === 'tasks' || meta.objective_id) { loadObjectives(); loadIterations(); }
    if (entity === 'attributes') { loadAttributes(); loadTargets(); }
    loadOverview();
  }, [lastMutation, projectId, loadProject, loadIterations, loadObjectives, loadAttributes, loadTargets, loadOverview]);

  // --- Projet
  const patchProject = useCallback((fields) => attempt('Mise à jour du projet', async () => {
    const updated = await apiService.patch(`/projects/${projectId}`, fields);
    if (updated) setProject(updated); else await loadProject();
    return updated;
  }), [attempt, projectId, loadProject]);

  const uploadHero = useCallback((file) => attempt("Image du projet", async () => {
    const form = new FormData();
    form.append('file', file);
    const updated = await apiService.uploadFile(`projects/${projectId}/hero-image`, form, null, 'POST');
    if (updated) setProject(updated);
  }), [attempt, projectId]);

  const removeHero = useCallback(() => attempt("Image du projet", async () => {
    const updated = await apiService.delete(`/projects/${projectId}/hero-image`);
    if (updated) setProject(updated);
  }), [attempt, projectId]);

  const setContacts = (contacts) => setProject((p) => (p ? { ...p, client_contacts: list(contacts) } : p));
  const addContact = useCallback((contactId, role) => attempt('Ajout du contact', async () => {
    setContacts(await apiService.addProjectClientContact(projectId, { contact_id: contactId, role_in_project: role?.trim() || null }));
  }), [attempt, projectId]);
  const updateContactRole = useCallback((contactId, role) => attempt('Rôle du contact', async () => {
    setContacts(await apiService.updateProjectClientContact(projectId, contactId, { role_in_project: role?.trim() || null }));
  }), [attempt, projectId]);
  const removeContact = useCallback((contactId) => attempt('Retrait du contact', async () => {
    setContacts(await apiService.removeProjectClientContact(projectId, contactId));
  }), [attempt, projectId]);

  // --- Équipe : la mention du projet dans le focus d'un rôle.
  const setMember = useCallback((roleId, employeeId, present) => attempt("Mise à jour de l'équipe", async () => {
    const entry = focus.find((f) => Number(f.circle_id) === Number(roleId) && Number(f.employee_id) === Number(employeeId));
    const text = present ? withMention(entry?.focus_text, project) : withoutMention(entry?.focus_text, project.id);
    if (text === (entry?.focus_text || null)) return;
    await apiService.put('/circle-focus', { circle_id: Number(roleId), employee_id: Number(employeeId), focus_text: text });
    await loadFocus();
    loadOverview();
    loadDomains(); // le focus décide qui tient les domaines à effet du projet
  }), [attempt, focus, project, loadFocus, loadOverview, loadDomains]);

  // --- Motif d'un changement de roadmap ou de budget (domaine tenu par un rôle sur le projet), demandé avant
  // d'envoyer avec ce qui change. Sans titulaire, rien n'est demandé ; si un titulaire vient d'apparaître, le serveur
  // répond 428 et apiService demande le motif lui-même (fenêtre de l'appli).
  const askReason = useCallback((info) => new Promise((resolve) => {
    reasonAnswer.current?.(null);
    reasonAnswer.current = resolve;
    setReasonRequest(info);
  }), []);
  const answerReason = useCallback((value) => {
    const resolve = reasonAnswer.current;
    reasonAnswer.current = null;
    setReasonRequest(null);
    resolve?.(value);
  }, []);
  // En proposition, la roadmap se construit (kickoff) : rien n'est tracé avant le passage en cours.
  const proposal = project?.status === 'proposition';
  const traced = useCallback((keys) => (proposal ? [] : keys.filter((key) => domains?.[key]?.holders?.length)),
    [domains, proposal]);
  const withReason = useCallback(async (keys, info, send) => {
    const cancelled = () => Object.assign(new Error('Changement annulé'), { cancelled: true });
    const held = traced(keys);
    let reason = null;
    if (held.length) {
      reason = await askReason({ ...info, domains: held });
      if (!reason) throw cancelled();
    }
    const result = await send(reason);
    if (reason) {
      loadChanges();
      loadSteering();
    }
    return result;
  }, [traced, askReason, loadChanges, loadSteering]);

  // --- Itérations et sprints
  const afterRoadmap = useCallback(() => { loadIterations(); loadOverview(); loadSteering(); },
    [loadIterations, loadOverview, loadSteering]);

  const addIteration = useCallback((fields) => attempt("Ajout de l'itération", async () => {
    const payload = { description: '', ...fields, name: fields.name || `Itération ${iterations.length + 1}` };
    const created = await withReason(['roadmap'], {
      title: `Ajouter l'itération « ${payload.name} »`, confirm: "Ajouter l'itération",
      lines: [`Du ${shortDay(payload.start_date)} au ${shortDay(payload.end_date)}`],
    }, (reason) => apiService.change('POST', `/projects/${projectId}/iterations`, payload, reason));
    await loadIterations();
    loadSteering();
    return created;
  }), [attempt, withReason, projectId, iterations.length, loadIterations, loadSteering]);

  const patchIteration = useCallback((id, fields) => attempt("Mise à jour de l'itération", async () => {
    const before = iterations.find((it) => it.id === id);
    const { keys, title, lines } = describeIterationChange(before, fields);
    const held = traced(keys);
    // Sans motif à demander, l'écran suit tout de suite ; sinon on attend le motif pour ne rien montrer d'annulé.
    if (!held.length) setIterations((items) => items.map((it) => (it.id === id ? { ...it, ...fields } : it)));
    try {
      await withReason(keys, { title, lines, confirm: 'Enregistrer le changement' },
        (reason) => apiService.change('PATCH', `/projects/${projectId}/iterations/${id}`, fields, reason));
    } finally {
      afterRoadmap();
    }
  }), [attempt, traced, withReason, iterations, projectId, afterRoadmap]);

  const deleteIteration = useCallback((id) => attempt("Suppression de l'itération", async () => {
    const before = iterations.find((it) => it.id === id);
    const sprints = before?.sprints?.length || 0;
    await withReason(['roadmap', 'budget'], {
      title: `Supprimer l'itération « ${before?.name || ''} »`, confirm: "Supprimer l'itération", danger: true,
      lines: [`Du ${shortDay(before?.start_date)} au ${shortDay(before?.end_date)}${sprints ? `, avec ses ${sprints} sprint${sprints > 1 ? 's' : ''}` : ''}.`],
    }, (reason) => apiService.change('DELETE', `/projects/${projectId}/iterations/${id}`, undefined, reason));
    afterRoadmap();
  }), [attempt, withReason, iterations, projectId, afterRoadmap]);

  const addSprint = useCallback((iteration) => attempt('Ajout du sprint', async () => {
    // Trois semaines à partir du lundi qui suit le dernier sprint ; nommé d'après l'itération (« Sprint CHF 2.3 »).
    const defaults = buildDefaultSprintPayload(iteration);
    const description = /\d/.test(iteration.name || '') ? `Sprint ${iteration.name}.${iteration.sprints.length + 1}` : defaults.description;
    const created = await apiService.post(`/projects/${projectId}/sprints`,
      { ...defaults, description, iteration_id: iteration.id, vision: '' });
    await loadIterations();
    return created;
  }), [attempt, projectId, loadIterations]);

  const patchSprint = useCallback((id, fields) => attempt('Mise à jour du sprint', async () => {
    setIterations((items) => items.map((it) => ({
      ...it, sprints: it.sprints.map((s) => (s.id === id ? { ...s, ...fields, name: fields.description ?? s.name } : s)),
    })));
    try {
      await apiService.patch(`/projects/${projectId}/sprints/${id}`, fields);
    } finally {
      afterRoadmap();
    }
  }), [attempt, projectId, afterRoadmap]);

  const deleteSprint = useCallback((id) => attempt('Suppression du sprint', async () => {
    await apiService.delete(`/projects/${projectId}/sprints/${id}`);
    afterRoadmap();
  }), [attempt, projectId, afterRoadmap]);

  // --- Objectifs (statut changé depuis la roadmap)
  const patchObjective = useCallback((id, fields) => attempt("Mise à jour de l'objectif", async () => {
    setObjectives((items) => items.map((o) => (o.id === id ? { ...o, ...fields } : o)));
    try {
      const saved = await apiService.patch(`/projects/${projectId}/objectives/${id}`, fields);
      if (saved?.id) setObjectives((items) => items.map((o) => (o.id === saved.id ? saved : o)));
    } catch (err) {
      loadObjectives();
      throw err;
    }
    loadIterations(); // les points réalisés des sprints et itérations bougent
  }), [attempt, projectId, loadObjectives, loadIterations]);

  // --- Jalons (objectifs marqués jalon, datés, hors sprint)
  const saveMilestone = useCallback((draft, existing) => attempt('Enregistrement du jalon', async () => {
    const payload = { name: draft.name, description: draft.description, owner_id: draft.owner_id, status: draft.status,
      sprint_id: null, is_milestone: true, milestone_date: draft.milestone_date };
    const moved = existing?.id && String(existing.milestone_date || '').slice(0, 10) !== String(draft.milestone_date || '').slice(0, 10);
    const info = existing?.id
      ? { title: `Modifier le jalon « ${draft.name} »`, confirm: 'Enregistrer le jalon',
        lines: [moved ? `Du ${shortDay(existing.milestone_date)} au ${shortDay(draft.milestone_date)}` : `Le ${shortDay(draft.milestone_date)}`] }
      : { title: `Poser le jalon « ${draft.name} »`, confirm: 'Poser le jalon', lines: [`Le ${shortDay(draft.milestone_date)}`] };
    const saved = await withReason(['roadmap'], info, (reason) => (existing?.id
      ? apiService.change('PATCH', `/projects/${projectId}/objectives/${existing.id}`, payload, reason)
      : apiService.change('POST', `/projects/${projectId}/objectives`,
        { ...payload, project_id: Number(projectId), effort: 0, blocked_by: [], comment: null }, reason)));
    setObjectives((items) => (existing?.id ? items.map((o) => (o.id === saved.id ? saved : o)) : [...items, saved]));
    loadOverview();
    loadSteering();
    return saved;
  }), [attempt, withReason, projectId, loadOverview, loadSteering]);

  const moveMilestone = useCallback((id, date) => attempt('Déplacement du jalon', async () => {
    const before = objectives.find((o) => o.id === id);
    const held = traced(['roadmap']);
    if (!held.length) setObjectives((items) => items.map((o) => (o.id === id ? { ...o, milestone_date: date } : o)));
    try {
      await withReason(['roadmap'], {
        title: `Déplacer le jalon « ${before?.name || ''} »`, confirm: 'Déplacer le jalon',
        lines: [`Du ${shortDay(before?.milestone_date)} au ${shortDay(date)}`],
      }, (reason) => apiService.change('PATCH', `/projects/${projectId}/objectives/${id}`,
        { is_milestone: true, milestone_date: date, sprint_id: null }, reason));
    } finally {
      loadObjectives();
      loadOverview();
    }
  }), [attempt, traced, withReason, objectives, projectId, loadObjectives, loadOverview]);

  const deleteMilestone = useCallback((id) => attempt('Suppression du jalon', async () => {
    const before = objectives.find((o) => o.id === id);
    await withReason(['roadmap'], {
      title: `Retirer le jalon « ${before?.name || ''} »`, confirm: 'Retirer le jalon', danger: true,
      lines: [`Il était au ${shortDay(before?.milestone_date)}.`],
    }, (reason) => apiService.change('DELETE', `/projects/${projectId}/objectives/${id}`, undefined, reason));
    setObjectives((items) => items.filter((o) => o.id !== id));
    loadOverview();
    loadSteering();
  }), [attempt, withReason, objectives, projectId, loadOverview, loadSteering]);

  // --- % de réalisation d'une itération (domaine Roadmap : son titulaire, ou tout le monde s'il n'y en a pas)
  const setProgress = useCallback((iterationId, percent, comment) => attempt('Réalisation de l’itération', async () => {
    await apiService.put(`/projects/${projectId}/iterations/${iterationId}/progress`, { percent, comment: comment || null });
    await loadSteering();
  }), [attempt, projectId, loadSteering]);

  // --- Cadre du projet (puces) : relève de la roadmap (son titulaire, ou tout le monde s'il n'y en a pas)
  const saveFrame = useCallback((items) => attempt('Cadre du projet', async () => {
    setFrame(await apiService.put(`/projects/${projectId}/frame`, { items }));
  }), [attempt, projectId]);

  // --- Facturabilité d'une itération (domaine à effet « Facturabilité »)
  const decideBillability = useCallback((iterationId, billable) => attempt('Facturabilité', async () => {
    const decided = await apiService.put(`/projects/${projectId}/iterations/${iterationId}/billability`, { billable });
    setBillability((prev) => ({ ...prev, [iterationId]: decided }));
    return decided;
  }), [attempt, projectId]);

  // --- Cibles d'attributs par itération
  const addTarget = useCallback((iterationId, payload) => attempt('Ajout de la cible', async () => {
    await apiService.createIterationAttributeTarget(iterationId, payload);
    await loadTargets();
  }), [attempt, loadTargets]);
  const updateTarget = useCallback((iterationId, targetId, payload) => attempt('Mise à jour de la cible', async () => {
    await apiService.updateIterationAttributeTarget(iterationId, targetId, payload);
    await loadTargets();
  }), [attempt, loadTargets]);
  const deleteTarget = useCallback((iterationId, targetId) => attempt('Suppression de la cible', async () => {
    await apiService.deleteIterationAttributeTarget(iterationId, targetId);
    await loadTargets();
  }), [attempt, loadTargets]);

  return {
    project, iterations, objectives, slots, employees, clients, circles, attributes, targets, focus, overview, error,
    domains, billability, steering, changes, frame, reasonRequest, answerReason,
    setProject, clearError: () => setError(null),
    reload: {
      project: loadProject, iterations: loadIterations, objectives: loadObjectives, overview: loadOverview,
      attributes: loadAttributes, targets: loadTargets, domains: loadDomains, billability: loadBillability,
      steering: loadSteering, changes: loadChanges, frame: loadFrame,
    },
    actions: {
      patchProject, uploadHero, removeHero, addContact, updateContactRole, removeContact, setMember,
      addIteration, patchIteration, deleteIteration, addSprint, patchSprint, deleteSprint,
      saveMilestone, moveMilestone, deleteMilestone, addTarget, updateTarget, deleteTarget, decideBillability, patchObjective,
      setProgress, saveFrame,
    },
  };
}
